/**
 * Rate Comparison Utilities
 * 
 * Provides intelligent rate comparison between contractor rates and Coca-Cola rates
 * to warn users when they might lose money on a service.
 */

/**
 * Compares contractor rates against Coca-Cola rates to determine profitability
 * 
 * @param {Object} params - Comparison parameters
 * @param {string} params.contractorRateType - 'FLAT' or 'HOURLY'
 * @param {number|string} params.contractorRate - The contractor's rate
 * @param {Object} params.serviceType - Service type object with Coca-Cola rates
 * @returns {Object} Comparison result with willLoseMoney flag, message, and optional details
 */
export function compareRates({ contractorRateType, contractorRate, serviceType }) {
  const rate = parseFloat(contractorRate);
  
  if (isNaN(rate) || rate <= 0) {
    return { willLoseMoney: false };
  }

  // FLAT to FLAT comparison (most straightforward)
  if (contractorRateType === 'FLAT' && serviceType.flat_completion_rate) {
    const cocaColaRate = parseFloat(serviceType.flat_completion_rate);
    const profitMargin = ((cocaColaRate - rate) / cocaColaRate * 100).toFixed(1);
    
    if (rate >= cocaColaRate) {
      return {
        willLoseMoney: true,
        message: `⚠️ Warning: Contractor flat rate ($${rate}) equals or exceeds Coca-Cola rate ($${cocaColaRate})`,
        details: {
          profitMargin: profitMargin,
          estimatedHours: null
        }
      };
    }
    
    // Warn if profit margin is too low (less than 10%)
    if (profitMargin < 10) {
      return {
        willLoseMoney: true,
        message: `⚠️ Low profit margin: Contractor rate ($${rate}) vs Coca-Cola rate ($${cocaColaRate})`,
        details: {
          profitMargin: profitMargin,
          estimatedHours: null
        }
      };
    }
    
    return { willLoseMoney: false };
  }

  // HOURLY to HOURLY comparison
  if (contractorRateType === 'HOURLY' && serviceType.hourly_rate) {
    const cocaColaRate = parseFloat(serviceType.hourly_rate);
    const profitMargin = ((cocaColaRate - rate) / cocaColaRate * 100).toFixed(1);
    
    if (rate >= cocaColaRate) {
      return {
        willLoseMoney: true,
        message: `⚠️ Warning: Contractor hourly rate ($${rate}/hr) equals or exceeds Coca-Cola rate ($${cocaColaRate}/hr)`,
        details: {
          profitMargin: profitMargin,
          estimatedHours: null
        }
      };
    }
    
    // Warn if profit margin is too low (less than 10%)
    if (profitMargin < 10) {
      return {
        willLoseMoney: true,
        message: `⚠️ Low profit margin: Contractor rate ($${rate}/hr) vs Coca-Cola rate ($${cocaColaRate}/hr)`,
        details: {
          profitMargin: profitMargin,
          estimatedHours: null
        }
      };
    }
    
    return { willLoseMoney: false };
  }

  // CROSS-TYPE COMPARISON: Contractor is FLAT, but Coca-Cola is HOURLY
  if (contractorRateType === 'FLAT' && serviceType.hourly_rate && !serviceType.flat_completion_rate) {
    const cocaColaHourlyRate = parseFloat(serviceType.hourly_rate);
    // Assume average job takes 2 hours for cross-comparison
    const estimatedHours = 2;
    const estimatedCocaColaRevenue = cocaColaHourlyRate * estimatedHours;
    const profitMargin = ((estimatedCocaColaRevenue - rate) / estimatedCocaColaRevenue * 100).toFixed(1);
    
    if (rate >= estimatedCocaColaRevenue) {
      return {
        willLoseMoney: true,
        message: `⚠️ Warning: Contractor flat rate ($${rate}) may exceed Coca-Cola hourly revenue (est. $${estimatedCocaColaRevenue.toFixed(2)} for ${estimatedHours}hrs @ $${cocaColaHourlyRate}/hr)`,
        details: {
          profitMargin: profitMargin,
          estimatedHours: estimatedHours
        }
      };
    }
    
    if (profitMargin < 10) {
      return {
        willLoseMoney: true,
        message: `⚠️ Low profit margin: Contractor flat rate ($${rate}) vs est. Coca-Cola hourly revenue ($${estimatedCocaColaRevenue.toFixed(2)})`,
        details: {
          profitMargin: profitMargin,
          estimatedHours: estimatedHours
        }
      };
    }
    
    return { willLoseMoney: false };
  }

  // CROSS-TYPE COMPARISON: Contractor is HOURLY, but Coca-Cola is FLAT
  if (contractorRateType === 'HOURLY' && serviceType.flat_completion_rate && !serviceType.hourly_rate) {
    const cocaColaFlatRate = parseFloat(serviceType.flat_completion_rate);
    // Calculate break-even hours
    const breakEvenHours = (cocaColaFlatRate / rate).toFixed(1);
    // Assume average job takes 2 hours
    const estimatedHours = 2;
    const estimatedContractorCost = rate * estimatedHours;
    const profitMargin = ((cocaColaFlatRate - estimatedContractorCost) / cocaColaFlatRate * 100).toFixed(1);
    
    if (estimatedContractorCost >= cocaColaFlatRate) {
      return {
        willLoseMoney: true,
        message: `⚠️ Warning: Contractor hourly rate ($${rate}/hr) may exceed Coca-Cola flat rate ($${cocaColaFlatRate}) if job takes more than ${breakEvenHours} hours`,
        details: {
          profitMargin: profitMargin,
          estimatedHours: breakEvenHours
        }
      };
    }
    
    if (profitMargin < 10) {
      return {
        willLoseMoney: true,
        message: `⚠️ Low profit margin: Est. contractor cost ($${estimatedContractorCost.toFixed(2)} @ ${estimatedHours}hrs) vs Coca-Cola rate ($${cocaColaFlatRate})`,
        details: {
          profitMargin: profitMargin,
          estimatedHours: estimatedHours
        }
      };
    }
    
    return { willLoseMoney: false };
  }

  // No comparison possible
  return { willLoseMoney: false };
}

/**
 * Calculate profit margin percentage
 * 
 * @param {number} revenue - Revenue from Coca-Cola
 * @param {number} cost - Cost to pay contractor
 * @returns {number} Profit margin as percentage
 */
export function calculateProfitMargin(revenue, cost) {
  if (revenue <= 0) return 0;
  return ((revenue - cost) / revenue * 100);
}

/**
 * Check if a rate configuration is profitable
 * 
 * @param {number} revenue - Revenue from Coca-Cola
 * @param {number} cost - Cost to pay contractor
 * @param {number} minMargin - Minimum acceptable profit margin (default 10%)
 * @returns {boolean} True if profitable
 */
export function isProfitable(revenue, cost, minMargin = 10) {
  const margin = calculateProfitMargin(revenue, cost);
  return margin >= minMargin;
}

